"use client";

import { ALGOLIA_ANALYTICS_INDEX } from "@raumagarn/algolia";
import { useQuery } from "@tanstack/react-query";
import { tryCatch } from "@trydig/utils";
import { useMemo } from "react";
import { algoliaClient } from "~/features/algolia/client";

interface PopularQuery {
	query: string;
	popularity: number;
	objectID: string;
}

export const useAlgoliaAutoCompleteSuggestions = (currentQuery: string, limit = 6) => {
	const shouldSearch = useMemo(() => {
		return currentQuery.trim().length >= 2;
	}, [currentQuery]);

	return useQuery({
		queryKey: ["autocomplete-suggestions", currentQuery, limit],
		queryFn: async (): Promise<string[]> => {
			if (!shouldSearch) return [];

			const [data, error] = await tryCatch(
				algoliaClient.searchSingleIndex<PopularQuery>({
					indexName: ALGOLIA_ANALYTICS_INDEX,
					searchParams: {
						query: currentQuery,
						hitsPerPage: 50,
						attributesToRetrieve: ["query", "popularity"],
					},
				}),
			);

			if (error) {
				console.error("Failed to fetch autocomplete suggestions:", error);
				return [];
			}

			const suggestions = data.hits
				.filter((hit) => {
					// Filter for queries that:
					// 1. Start with current query (prefix matching)
					// 2. Are different from current query (no exact matches)
					// 3. Have some popularity
					const query = hit.query.toLowerCase();
					const currentLower = currentQuery.toLowerCase().trim();
					return query.startsWith(currentLower) && query !== currentLower && hit.popularity > 0;
				})
				.sort((a, b) => b.popularity - a.popularity)
				.slice(0, limit)
				.map((hit) => hit.query);

			return suggestions;
		},
		enabled: shouldSearch,
		staleTime: 2 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
	});
};
